import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from './dynamic-api.service';
import { AddIndexRequest, DynamicEntityDetail, DynamicEntityIndex, DynamicField } from './dynamic.models';

type SelectOption<T = string> = { label: string; value: T };
type FilterCondition = 'isNull' | 'isNotNull';

interface IndexFormState {
  name: string;
  keyColumns: string[];
  isUnique: boolean;
  filterColumn: string | null;
  filterCondition: FilterCondition | null;
  includedColumns: string[];
}

@Component({
  standalone: true,
  selector: 'ain-dynamic-index-editor',
  imports: [
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    ChipModule,
    TagModule,
    ConfirmDialogModule,
    FormsModule
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <section class="index-editor">
      <div class="section-header">
        <h3>{{ t('dynamic.index.title') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.index.info.title'"
          [descriptionKey]="'dynamic.index.info.description'"
          [bulletKeys]="[
            'dynamic.index.info.bullets.columns',
            'dynamic.index.info.bullets.unique',
            'dynamic.index.info.bullets.filtered'
          ]" />
      </div>

      @if (entity().isActive) {
        <div class="notice card active-notice" role="status">
          <strong>{{ t('dynamic.index.activeEntityNotice.title') }}</strong>
          <p>{{ t('dynamic.index.activeEntityNotice.description') }}</p>
        </div>
      }

      @if (!canViewIndexes()) {
        <ain-state type="empty" [title]="t('dynamic.index.title')" [message]="t('common.noPermission')" />
      } @else if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else {
        @if (!indexes().length) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
          @if (canCreateIndex()) {
            <div class="empty-actions">
              <button pButton type="button" [label]="t('dynamic.index.add')" (click)="openAddDialog()"></button>
            </div>
          }
        } @else {
          <div class="toolbar">
            @if (canCreateIndex()) {
              <button pButton type="button" [label]="t('dynamic.index.add')" (click)="openAddDialog()"></button>
            }
          </div>

          <p-table [value]="indexes()" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('dynamic.index.name') }}</th>
                <th>{{ t('dynamic.index.columns') }}</th>
                <th>{{ t('dynamic.index.isUnique') }}</th>
                <th>{{ t('dynamic.index.filter') }}</th>
                <th>{{ t('dynamic.index.includedColumns') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th style="width: 8rem"></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-index>
              <tr>
                <td>{{ index.name }}</td>
                <td>{{ columnLabels(index.columns) }}</td>
                <td>{{ index.isUnique ? t('common.yes') : t('common.no') }}</td>
                <td>{{ filterLabel(index.filterExpression) }}</td>
                <td>{{ columnLabels(index.includedColumns) }}</td>
                <td>
                  <p-tag
                    [value]="index.isActive ? t('dynamic.index.status.active') : t('dynamic.index.status.inactive')"
                    [severity]="index.isActive ? 'success' : 'secondary'" />
                </td>
                <td class="row-actions">
                  @if (canDeleteIndex()) {
                    <button pButton type="button" text severity="danger" [label]="t('dynamic.index.delete')" (click)="confirmDelete(index)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      <p-dialog
        [header]="t('dynamic.index.add')"
        [(visible)]="indexDialogVisible"
        [modal]="true"
        [style]="{ width: 'min(640px, 94vw)' }"
        (onHide)="resetIndexForm()">
        <div class="dialog-form">
          <ain-form-field [label]="t('dynamic.index.name')" [hint]="t('dynamic.entities.namePatternHint')">
            <input pInputText [(ngModel)]="indexForm.name" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.index.orderedColumns')" [hint]="t('dynamic.index.orderedColumnsHint')">
            <div class="ordered-columns">
              <div class="ordered-picker">
                <p-select
                  [options]="availableKeyColumnOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="pendingKeyColumn"
                  [filter]="true"
                  [showClear]="true"
                  appendTo="body" />
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  [label]="t('dynamic.index.addColumn')"
                  [disabled]="!pendingKeyColumn"
                  (click)="addKeyColumn()"></button>
              </div>

              @if (indexForm.keyColumns.length) {
                <div class="column-chips">
                  @for (column of indexForm.keyColumns; track column; let idx = $index) {
                    <p-chip
                      [label]="fieldLabel(column)"
                      [removable]="true"
                      (onRemove)="removeKeyColumn(idx)" />
                  }
                </div>
              } @else {
                <p class="field-hint">{{ t('dynamic.index.columnsRequired') }}</p>
              }
            </div>
          </ain-form-field>

          <div class="checkbox-row">
            <label>
              <p-checkbox binary="true" [(ngModel)]="indexForm.isUnique" />
              <span>{{ t('dynamic.index.isUnique') }}</span>
            </label>
          </div>

          <ain-form-field [label]="t('dynamic.index.filter')" [hint]="t('dynamic.index.filterHint')">
            <div class="filter-row">
              <p-select
                [options]="fieldOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="indexForm.filterColumn"
                [filter]="true"
                [showClear]="true"
                [placeholder]="t('dynamic.index.filterColumnPlaceholder')"
                appendTo="body" />
              <p-select
                [options]="filterConditionOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="indexForm.filterCondition"
                [showClear]="true"
                [disabled]="!indexForm.filterColumn"
                [placeholder]="t('dynamic.index.filterConditionPlaceholder')"
                appendTo="body" />
            </div>
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.index.includedColumns')" [hint]="t('dynamic.index.includedColumnsHint')">
            <p-multiSelect
              [options]="availableIncludedColumnOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="indexForm.includedColumns"
              [filter]="true"
              [showClear]="true"
              appendTo="body" />
          </ain-form-field>
        </div>

        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="indexDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="savingIndex()" (click)="saveIndex()"></button>
        </ng-template>
      </p-dialog>

      <p-confirmDialog />
    </section>
  `,
  styles: [`
    .index-editor { display: grid; gap: 12px; }
    .section-header { display: grid; gap: 8px; }
    .section-header h3 { margin: 0; }
    .toolbar, .empty-actions { display: flex; justify-content: flex-end; }
    .row-actions { display: flex; gap: 4px; flex-wrap: wrap; }
    .dialog-form { display: grid; gap: 12px; }
    .checkbox-row label { display: inline-flex; align-items: center; gap: 8px; }
    .notice.card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; background: #f9fafb; }
    .active-notice { border-color: #fde68a; background: #fffbeb; }
    .ordered-columns { display: grid; gap: 8px; }
    .ordered-picker { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .ordered-picker p-select { flex: 1 1 220px; }
    .column-chips { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-row { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .field-hint { margin: 0; color: #6b7280; font-size: 0.875rem; }
  `]
})
export class DynamicIndexEditorComponent {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);

  readonly entity = input.required<DynamicEntityDetail>();
  readonly refresh = output<void>();

  readonly indexes = signal<DynamicEntityIndex[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly savingIndex = signal(false);

  indexDialogVisible = false;
  indexForm: IndexFormState = this.emptyIndexForm();
  pendingKeyColumn: string | null = null;

  private readonly entityId = computed(() => this.entity().id);
  private readonly fields = computed(() => this.entity().fields ?? []);

  readonly fieldOptions = computed<SelectOption[]>(() =>
    this.fields().map(field => ({
      label: `${field.displayName} (${field.name})`,
      value: field.name
    }))
  );

  readonly availableKeyColumnOptions = computed<SelectOption[]>(() => {
    const selected = new Set(this.indexForm.keyColumns.map(item => item.toLowerCase()));
    return this.fieldOptions().filter(item => !selected.has(item.value.toLowerCase()));
  });

  readonly availableIncludedColumnOptions = computed<SelectOption[]>(() => {
    const keyColumns = new Set(this.indexForm.keyColumns.map(item => item.toLowerCase()));
    return this.fieldOptions().filter(item => !keyColumns.has(item.value.toLowerCase()));
  });

  readonly filterConditionOptions = computed<SelectOption<FilterCondition>[]>(() => [
    { label: this.t('dynamic.index.filterCondition.isNull'), value: 'isNull' },
    { label: this.t('dynamic.index.filterCondition.isNotNull'), value: 'isNotNull' }
  ]);

  constructor() {
    effect(() => {
      const id = this.entityId();
      if (id) {
        this.loadIndexes(id);
      }
    });
  }

  loadIndexes(entityId: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.api.listIndexes(entityId).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: items => this.indexes.set(items),
      error: err => {
        this.indexes.set([]);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  openAddDialog(): void {
    this.indexForm = this.emptyIndexForm();
    this.pendingKeyColumn = null;
    this.indexDialogVisible = true;
  }

  addKeyColumn(): void {
    const column = this.pendingKeyColumn?.trim();
    if (!column) {
      return;
    }

    if (this.indexForm.keyColumns.some(item => item.toLowerCase() === column.toLowerCase())) {
      return;
    }

    this.indexForm = {
      ...this.indexForm,
      keyColumns: [...this.indexForm.keyColumns, column],
      includedColumns: this.indexForm.includedColumns.filter(item => item.toLowerCase() !== column.toLowerCase())
    };
    this.pendingKeyColumn = null;
  }

  removeKeyColumn(index: number): void {
    this.indexForm = {
      ...this.indexForm,
      keyColumns: this.indexForm.keyColumns.filter((_, idx) => idx !== index)
    };
  }

  saveIndex(): void {
    const name = this.indexForm.name.trim();
    if (!name || !this.indexForm.keyColumns.length) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.index.columnsRequired') });
      return;
    }

    const filterExpression = this.buildFilterExpression();
    if (this.indexForm.filterColumn && !filterExpression) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.index.filterConditionRequired') });
      return;
    }

    const request: AddIndexRequest = {
      name,
      columns: [...this.indexForm.keyColumns],
      isUnique: this.indexForm.isUnique,
      filterExpression: filterExpression ?? undefined,
      includedColumns: this.indexForm.includedColumns.length ? [...this.indexForm.includedColumns] : undefined
    };

    this.savingIndex.set(true);
    this.api.addIndex(this.entity().id, request).pipe(
      finalize(() => this.savingIndex.set(false))
    ).subscribe({
      next: () => {
        this.indexDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('dynamic.index.saved') });
        this.loadIndexes(this.entity().id);
        this.refresh.emit();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveActionError(err) })
    });
  }

  confirmDelete(index: DynamicEntityIndex): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.index.confirm.delete'),
      accept: () => this.deleteIndex(index.id)
    });
  }

  fieldLabel(columnName: string): string {
    const field = this.findField(columnName);
    return field ? `${field.displayName} (${field.name})` : columnName;
  }

  columnLabels(columns: string[] | null | undefined): string {
    if (!columns?.length) {
      return '—';
    }
    return columns.map(column => this.fieldLabel(column)).join(', ');
  }

  filterLabel(filterExpression: string | null | undefined): string {
    if (!filterExpression?.trim()) {
      return this.t('dynamic.index.filterNone');
    }

    const match = /^\[([a-z][a-z0-9_]*)\]\s+(IS\s+NOT\s+NULL|IS\s+NULL)$/i.exec(filterExpression.trim());
    if (!match) {
      return this.t('dynamic.index.filterNone');
    }

    const columnLabel = this.fieldLabel(match[1]);
    const condition = match[2].replace(/\s+/g, ' ').toUpperCase();
    if (condition === 'IS NULL') {
      return `${columnLabel} — ${this.t('dynamic.index.filterCondition.isNull')}`;
    }
    if (condition === 'IS NOT NULL') {
      return `${columnLabel} — ${this.t('dynamic.index.filterCondition.isNotNull')}`;
    }

    return this.t('dynamic.index.filterNone');
  }

  canViewIndexes(): boolean {
    return this.hasIndexAction('View');
  }

  canCreateIndex(): boolean {
    return this.hasIndexAction('Create');
  }

  canDeleteIndex(): boolean {
    return this.hasIndexAction('Delete');
  }

  resetIndexForm(): void {
    this.indexForm = this.emptyIndexForm();
    this.pendingKeyColumn = null;
  }

  private deleteIndex(indexId: string): void {
    this.api.deleteIndex(indexId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.index.deleted') });
        this.loadIndexes(this.entity().id);
        this.refresh.emit();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveActionError(err) })
    });
  }

  private buildFilterExpression(): string | null {
    const column = this.indexForm.filterColumn?.trim();
    const condition = this.indexForm.filterCondition;
    if (!column || !condition) {
      return null;
    }

    const normalizedColumn = column.toLowerCase();
    return condition === 'isNull'
      ? `[${normalizedColumn}] IS NULL`
      : `[${normalizedColumn}] IS NOT NULL`;
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code) {
      const key = `dynamic.index.errors.${code}`;
      const translated = this.t(key);
      if (translated !== key) {
        return translated;
      }
    }

    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null;
      const message = typeof body?.['Message'] === 'string' ? body['Message'].trim() : '';
      if (message) {
        return message;
      }
    }

    return this.t('common.saveFailed');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }

  private findField(columnName: string): DynamicField | undefined {
    return this.fields().find(field => field.name.toLowerCase() === columnName.toLowerCase());
  }

  private emptyIndexForm(): IndexFormState {
    return {
      name: '',
      keyColumns: [],
      isUnique: false,
      filterColumn: null,
      filterCondition: null,
      includedColumns: []
    };
  }

  private hasIndexAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.indexes');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
